/**
 * Supabase Client for Portal
 *
 * Handles authentication and database access from the frontend.
 * Uses cookie storage with subdomain sharing so auth works across
 * my.aigentswork.com and aigentswork.com/view/
 */

import { createClient, type Session, type User } from '@supabase/supabase-js';
import { createSignal } from 'solid-js';

// Environment variables (set via Vite)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

/**
 * Cookie storage for Supabase auth
 * Stores tokens in cookies with domain=.aigentswork.com for subdomain sharing
 */
const COOKIE_OPTIONS = {
  domain: import.meta.env.PROD ? '.aigentswork.com' : undefined, // Share across subdomains in prod
  path: '/',
  sameSite: 'lax' as const,
  secure: import.meta.env.PROD, // HTTPS only in production
  maxAge: 60 * 60 * 24 * 365, // 1 year
};

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const parts = [`${name}=${encodeURIComponent(value)}`];
  if (COOKIE_OPTIONS.domain) parts.push(`domain=${COOKIE_OPTIONS.domain}`);
  parts.push(`path=${COOKIE_OPTIONS.path}`);
  parts.push(`max-age=${COOKIE_OPTIONS.maxAge}`);
  parts.push(`samesite=${COOKIE_OPTIONS.sameSite}`);
  if (COOKIE_OPTIONS.secure) parts.push('secure');
  document.cookie = parts.join('; ');
}

function removeCookie(name: string): void {
  if (typeof document === 'undefined') return;
  const parts = [`${name}=`, 'max-age=0'];
  if (COOKIE_OPTIONS.domain) parts.push(`domain=${COOKIE_OPTIONS.domain}`);
  parts.push(`path=${COOKIE_OPTIONS.path}`);
  document.cookie = parts.join('; ');
}

const cookieStorage = {
  getItem: (key: string): string | null => getCookie(key),
  setItem: (key: string, value: string): void => setCookie(key, value),
  removeItem: (key: string): void => removeCookie(key),
};

// Create Supabase client with cookie storage for subdomain auth sharing
export const supabase =
  supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          storage: cookieStorage,
          storageKey: 'sb-auth-token',
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
        },
      })
    : null;

// Check if we're in E2E test mode (no Supabase configured)
const isE2ETestMode = !supabaseUrl || !supabaseAnonKey;

// Auth state signals (SolidJS reactive)
// In E2E test mode, create a mock user so pages render without auth
const [user, setUser] = createSignal<User | null>(
  isE2ETestMode
    ? ({
        id: 'e2e-test-user',
        email: 'e2e@test.local',
        user_metadata: { name: 'E2E Test User' },
        app_metadata: {},
        aud: 'authenticated',
        created_at: new Date().toISOString(),
      } as User)
    : null,
);
const [session, setSession] = createSignal<Session | null>(null);
// In E2E test mode, don't show loading since we have a mock user
const [loading, setLoading] = createSignal(!isE2ETestMode);
const [error, setError] = createSignal<string | null>(null);

// Export reactive state
export { error, loading, session, user };

// Store subscription reference for cleanup
let authSubscription: { unsubscribe: () => void } | null = null;

/**
 * Initialize auth state listener
 * Call this once in your app root
 */
export async function initAuth() {
  if (!supabase) {
    console.warn('Supabase not configured. Auth disabled.');
    setLoading(false);
    return;
  }

  try {
    // Set up auth state change listener FIRST
    // This catches the INITIAL_SESSION event properly
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      console.log('Auth state changed:', event);
      setSession(newSession);
      setUser(newSession?.user ?? null);

      // Clear loading state when we get the initial session or sign in/out
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        setLoading(false);
      }
    });

    authSubscription = subscription;

    // Also get the session synchronously in case the event was missed
    const {
      data: { session: initialSession },
    } = await supabase.auth.getSession();

    // Only update if we haven't already gotten a session from the listener
    if (loading()) {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
    }
  } catch (err) {
    console.error('Auth initialization error:', err);
    setError(err instanceof Error ? err.message : 'Auth initialization failed');
    setLoading(false);
  }
}

/**
 * Cleanup auth subscription - call this when app unmounts
 */
export function cleanupAuth() {
  if (authSubscription) {
    authSubscription.unsubscribe();
    authSubscription = null;
  }
}

/**
 * Sign in with OAuth provider (Google, GitHub, etc.)
 */
export async function signInWithProvider(provider: 'google' | 'github' | 'discord' | 'microsoft') {
  if (!supabase) {
    setError('Supabase not configured');
    return { error: new Error('Supabase not configured') };
  }

  setLoading(true);
  setError(null);

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) {
    setError(error.message);
    setLoading(false);
  }

  return { data, error };
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email: string, password: string) {
  if (!supabase) {
    setError('Supabase not configured');
    return { error: new Error('Supabase not configured') };
  }

  setLoading(true);
  setError(null);

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    setError(error.message);
  }

  setLoading(false);
  return { data, error };
}

/**
 * Sign up with email and password
 */
export async function signUpWithEmail(email: string, password: string) {
  if (!supabase) {
    setError('Supabase not configured');
    return { error: new Error('Supabase not configured') };
  }

  setLoading(true);
  setError(null);

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) {
    setError(error.message);
  }

  setLoading(false);
  return { data, error };
}

/**
 * Sign in with OTP (One-Time Password) sent to email
 * Note: Despite the name, Supabase sends a 6-digit code by default.
 * The email template in Supabase dashboard determines whether a code
 * or clickable link is sent.
 */
export async function signInWithMagicLink(email: string) {
  if (!supabase) {
    setError('Supabase not configured');
    return { error: new Error('Supabase not configured') };
  }

  setLoading(true);
  setError(null);

  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      // This redirect URL is used if Supabase is configured to send magic links
      emailRedirectTo: `${window.location.origin}/auth/callback`,
      // Don't create user if they don't exist (existing users only)
      // Set to true if you want to allow sign-up via OTP
      shouldCreateUser: true,
    },
  });

  if (error) {
    setError(error.message);
  }

  setLoading(false);
  return { data, error };
}

/**
 * Sign out
 */
export async function signOut() {
  if (!supabase) {
    return { error: new Error('Supabase not configured') };
  }

  setLoading(true);
  const { error } = await supabase.auth.signOut();

  if (error) {
    setError(error.message);
  }

  setLoading(false);
  return { error };
}

/**
 * Get access token for API calls
 */
export async function getAccessToken(): Promise<string | null> {
  const currentSession = session();
  if (currentSession?.access_token) {
    return currentSession.access_token;
  }

  if (!supabase) return null;

  // Try to refresh
  const {
    data: { session: refreshedSession },
  } = await supabase.auth.refreshSession();
  return refreshedSession?.access_token ?? null;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!user();
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string) {
  if (!supabase) {
    setError('Supabase not configured');
    return { error: new Error('Supabase not configured') };
  }

  setLoading(true);
  setError(null);

  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
  });

  if (error) {
    setError(error.message);
  }

  setLoading(false);
  return { data, error };
}

/**
 * Update user password (after reset)
 */
export async function updatePassword(newPassword: string) {
  if (!supabase) {
    setError('Supabase not configured');
    return { error: new Error('Supabase not configured') };
  }

  setLoading(true);
  setError(null);

  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) {
    setError(error.message);
  }

  setLoading(false);
  return { data, error };
}
